import omit from 'lodash/omit';
import { Conversation, Conversations, Message } from './types';

export enum ActionType {
    SET_USER = 'SET_USER',
    SET_CONVERSATIONS = 'SET_CONVERSATIONS',
    SET_CONVERSATION = 'SET_CONVERSATION',
    NEW_CONVERSATION = 'NEW_CONVERSATION',
    ADD_MESSAGES = 'ADD_MESSAGES',
    NEW_MESSAGE = 'NEW_MESSAGE',
    UPDATE_MESSAGE = 'UPDATE_MESSAGE',
    SET_OLDEST_MESSAGE = 'SET_OLDEST_MESSAGE',
}

export type Action =
    | { type: ActionType.SET_USER; payload: { uuid: string } | null }
    | {
          type: ActionType.SET_CONVERSATIONS;
          payload: Conversation[];
      }
    | {
          type: ActionType.SET_CONVERSATION | ActionType.NEW_CONVERSATION;
          payload: Conversation;
      }
    | {
          type: ActionType.ADD_MESSAGES;
          payload: {
              conversationUuid: string;
              messages: Message[];
          };
      }
    | {
          type: ActionType.NEW_MESSAGE | ActionType.UPDATE_MESSAGE;
          payload: Message;
      }
    | {
          type: ActionType.SET_OLDEST_MESSAGE;
          payload: {
              conversationUuid: string;
              oldestMessageUuid: string;
          };
      };

export type State = Readonly<{
    user: {
        uuid: string;
    } | null;
    conversations: readonly Conversation[];
    messages: Readonly<Record<string, readonly Message[]>>;
    oldestMessageUuid: Readonly<Record<string, string>>;
}>;

export const initialState: State = {
    user: null,
    conversations: [],
    messages: {},
    oldestMessageUuid: {},
};

export function reducer(state: State = initialState, action: Action): State {
    switch (action.type) {
        case ActionType.SET_USER: {
            const user = action.payload;

            if (user === null) {
                return initialState;
            }

            if (user.uuid !== state.user?.uuid) {
                return {
                    ...initialState,
                    user,
                };
            }

            return { ...state, user: action.payload };
        }
        case ActionType.SET_CONVERSATIONS:
            return { ...state, conversations: action.payload.map(c => omit(c, 'newestMessage')) };
        case ActionType.NEW_CONVERSATION:
        case ActionType.SET_CONVERSATION: {
            const conversation = omit(action.payload, 'newestMessage');

            const conversationIndex = state.conversations.findIndex(
                c => c.uuid === conversation.uuid,
            );

            if (conversationIndex === -1) {
                return { ...state, conversations: [...state.conversations, conversation] };
            }

            return {
                ...state,
                conversations: replaceAtIndex(state.conversations, conversation, conversationIndex),
            };
        }
        case ActionType.ADD_MESSAGES: {
            const { conversationUuid, messages } = action.payload;

            let messagesForState = messages;
            if (state.messages[conversationUuid]) {
                // hash to avoid nested loop lookups inside dedup flow
                const existingMessageUuids = state.messages[conversationUuid].reduce(
                    (accum, { uuid }) => Object.assign(accum, { [uuid]: true }),
                    {},
                );
                messagesForState = [
                    ...state.messages[conversationUuid],
                    ...messages.reduce((accum: Message[], message: Message) => {
                        if (!existingMessageUuids[message.uuid]) {
                            accum.push(message);
                        }
                        return accum;
                    }, []),
                ];
            }

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationUuid]: messagesForState,
                },
            };
        }
        case ActionType.NEW_MESSAGE: {
            const { conversationUuid, uuid } = action.payload;

            if (state.messages[conversationUuid]?.find(m => m.uuid === uuid)) {
                return state;
            }

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationUuid]: [
                        action.payload,
                        ...(state.messages[conversationUuid] || []),
                    ],
                },
            };
        }
        case ActionType.UPDATE_MESSAGE: {
            const { conversationUuid, uuid, content, readAt } = action.payload;

            const messageIndex = state.messages[conversationUuid]?.findIndex(m => m.uuid === uuid);

            if (messageIndex === undefined || messageIndex === -1) {
                return state;
            }

            const messages = state.messages[conversationUuid];
            const oldMessage = messages[messageIndex];

            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationUuid]: replaceAtIndex(
                        messages,
                        {
                            ...oldMessage,
                            content: content ?? oldMessage.content,
                            readAt: readAt ?? oldMessage.readAt,
                        },
                        messageIndex,
                    ),
                },
            };
        }
        case ActionType.SET_OLDEST_MESSAGE: {
            const { conversationUuid, oldestMessageUuid } = action.payload;

            return {
                ...state,
                oldestMessageUuid: {
                    ...state.oldestMessageUuid,
                    [conversationUuid]: oldestMessageUuid,
                },
            };
        }
        default:
            return state;
    }
}

function replaceAtIndex<T>(arr: readonly T[], item: T, index: number): T[] {
    return [...arr.slice(0, index), item, ...arr.slice(index + 1)];
}

function memoizeSelector<V, S>(
    selectors: (state: S) => Array<unknown>,
    selector: (state: S) => V,
): (state: S) => V {
    let initialized = false;
    let cachedValue: V;
    let cachedInputs: Array<unknown> = [];

    return function (state: S) {
        const inputs = selectors(state);
        const changed = inputs.some((v, i) => v !== cachedInputs[i]);

        if (!changed && initialized) {
            return cachedValue;
        }

        initialized = true;
        cachedInputs = inputs;
        return (cachedValue = selector(state));
    };
}

const getConversations = memoizeSelector(
    (state: State) => [state.user?.uuid, state.conversations, state.messages],
    (state: State): Conversations => {
        const conversations = state.conversations.map(c => {
            const messages = getMessages(state, c.uuid);

            return {
                ...c,
                newestMessage: messages[0],
                hasUnreadMessages: messages.some(
                    msg => msg.receiverUuid === state.user?.uuid && !msg.readAt,
                ),
            };
        });

        conversations.sort((a, b) => {
            const aDate: Date = new Date(a.newestMessage ? a.newestMessage.sentAt : a.createdAt);
            const bDate: Date = new Date(b.newestMessage ? b.newestMessage.sentAt : b.createdAt);

            if (aDate < bDate) return 1;
            if (aDate > bDate) return -1;
            return 0;
        });

        return conversations;
    },
);

function getMessages(state: State, conversationUuid: string): readonly Message[] {
    const messages = state.messages[conversationUuid];
    if (!messages) {
        return [];
    }

    return messages;
}

export const Selectors = {
    getActiveUserUuid(state: State): string | null {
        return state.user && state.user.uuid;
    },
    getConversations,
    getUnreadConversations: memoizeSelector(
        (state: State) => [getConversations(state)],
        (state: State): Conversations =>
            getConversations(state).filter(c => c.hasUnreadMessages === true),
    ),
    getConversation(state: State, conversationUuid: string): Conversation | undefined {
        return state.conversations.find(c => c.uuid === conversationUuid);
    },
    getMessages,
    getOldestMessageUuid(state: State, conversationUuid: string): string | undefined {
        return state.oldestMessageUuid[conversationUuid];
    },
} as const;
